/**
 * useComposePage.ts — ComposePage logic hook (state, effects, handlers)
 * extracted from ComposePage.tsx (god-component split, zero behaviour change).
 * Returns the values the page JSX consumes.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sanitizeEmailHtml, getRecipientEmail } from '@quotech/ui'
import {
  useEmail,
  useEmailAccounts,
  useOutlookOAuthResultToast,
  useOutlookStatus,
  type EmailAttachment,
} from '../hooks/useEmails'
import { useEmailDraftReply } from '../hooks/useEmailAI'
import { emailApiFetch } from '../lib/api'
import { emailPath } from '../lib/basePath'
import RichTextEditor, { type RichTextEditorHandle, typingAreaIsBlank } from '../components/RichTextEditor'
import { extractLinkedCardIds, toEmailHtml } from '../components/rich-text/emailHtml'
import { hasSignatureBlock, wrapSignatureLayout } from '../components/rich-text/signatureBlock'
import { wrapQuoteLayout } from '../components/rich-text/quoteBlock'
import { useToast } from '../../_internal/components/Toast'
import { newRequestId, reportClientIssue } from '../../_internal/services/integrationEvents'
import { useComposePendingStore } from '../stores/composePendingStore'
import { readDraft, writeDraft, clearDraft, makeSlotKey } from '../lib/draftStore'
import { invalidEmails } from '../lib/emailAddress'
import { takePendingQuoteSelection } from '../lib/quoteSelection'
import { formatResurfaceTime } from '../lib/resurfaceTimes'
import { shouldHydrateServerBody, attachmentsToAdd } from '../lib/composeResume'
import {
  saveDraftAttachments,
  loadDraftAttachments,
  clearDraftAttachments,
} from '../lib/draftAttachmentStore'
import {
  listServerDrafts,
  upsertServerDraft,
  deleteServerDraft,
  uploadServerDraftAttachment,
  deleteServerDraftAttachment,
  downloadServerDraftAttachment,
  type ServerDraft,
} from '../lib/serverDraftStore'
import { newDraftId } from '../compose/composeDockStore'
import {
  chooseSeedFromAccount,
  getLastFromAccountId,
  getPinnedFromAccountId,
  recordLastFromAccountLocal,
} from '../compose/fromDefaultSync'
import {
  getQuotechBrandingDefault,
  syncBrandingDefaultFromServer,
} from '../compose/brandingDefaultSync'
import { tlsKey } from '../../_internal/utils/tenantStorage'
import { useEmailDraftSync } from '../hooks/useEmailDraftSync'
import { AUTOSAVE_INTERVAL_MS, csvToList, bareAddress, dedupeAddresses, htmlToPlainText, hydrateSignatureFromDb, mergeLinkedCardIds, resolveSignatureWithDefault, fetchDurableSignaturesByAccount } from './_composeHelpers'
import type { PersistedDraft, SignatureIdentity } from './_composeHelpers'
import type { ComposePageProps } from './ComposePage'

// Tenant-scoped flag for the "drafts auto-delete in 14 days" notice —
// once the operator ticks "don't show again" it stays dismissed.
const DRAFT_EXPIRY_NOTICE_KEY = tlsKey('email.compose.draftExpiryNotice.dismissed.v1')

export function useComposePage(props: ComposePageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editorRef = useRef<RichTextEditorHandle | null>(null)

  const replyToId =
    props.replyToIdOverride !== undefined ? props.replyToIdOverride : searchParams.get('reply')
  const forwardId =
    props.forwardIdOverride !== undefined ? props.forwardIdOverride : searchParams.get('forward')
  const replyAllId =
    props.replyAllIdOverride !== undefined ? props.replyAllIdOverride : searchParams.get('reply_all')
  const intentEmailId = replyToId || replyAllId || forwardId
  const {
    email: replyEmail,
    loading: replyEmailLoading,
    error: replyEmailError,
    refetch: refetchReplyEmail,
  } = useEmail(intentEmailId || null)

  const intent: PersistedDraft['intent'] = replyToId
    ? 'reply'
    : replyAllId
      ? 'reply_all'
      : forwardId
        ? 'forward'
        : 'new'

  // ---------------------------------------------------------------
  // Per-window draft id (multiple new drafts). A `new` compose owns
  // an id so the operator can keep SEVERAL independent new messages
  // in flight (dock tabs + page mode); reply/forward stay keyed by
  // their source email id, so draftId is null for them.
  //   - dock      → props.draftIdOverride (the window's id)
  //   - page mode → ?draft= URL param, else a freshly-minted id that
  //                 we stamp back into the URL so a refresh resumes
  //                 THIS draft instead of opening a blank one.
  // `slotKey` is the single source of truth for the localStorage
  // slot, the IndexedDB attachment bucket, AND the server draft
  // `client_ref` — keeping all three layers addressing the same
  // draft.
  // ---------------------------------------------------------------
  const urlDraftId = searchParams.get('draft')
  const generatedDraftIdRef = useRef<string | null>(null)
  const draftId = useMemo<string | null>(() => {
    if (intent !== 'new') return null
    if (props.draftIdOverride != null) return props.draftIdOverride
    if (urlDraftId) return urlDraftId
    if (!generatedDraftIdRef.current) generatedDraftIdRef.current = newDraftId()
    return generatedDraftIdRef.current
  }, [intent, props.draftIdOverride, urlDraftId])
  const slotKey = useMemo(
    () => makeSlotKey(intent, intentEmailId || null, draftId),
    [intent, intentEmailId, draftId],
  )

  const draftUrlStampedRef = useRef(false)
  useEffect(() => {
    if (props.embed) return
    if (intent !== 'new' || urlDraftId) return
    if (draftUrlStampedRef.current || !draftId) return
    draftUrlStampedRef.current = true
    navigate(`${emailPath('/compose')}?draft=${encodeURIComponent(draftId)}`, {
      replace: true,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [to, setTo] = useState<string[]>(() =>
    props.initialToOverride !== undefined
      ? props.initialToOverride
      : csvToList(searchParams.get('to') ?? ''),
  )
  const [cc, setCc] = useState<string[]>([])
  const [bcc, setBcc] = useState<string[]>([])
  const [subject, setSubject] = useState(
    props.initialSubjectOverride !== undefined
      ? props.initialSubjectOverride
      : (searchParams.get('subject') ?? ''),
  )
  const [bodyHtml, setBodyHtml] = useState<string>(
    () => props.initialBodyOverride ?? '',
  )
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  // Send-as mailbox + outbound priority (compose metadata MR). The
  // backend send endpoint already accepts ``email_account_id`` (resolves
  // + authorises the mailbox) and now ``importance``. ``fromAccountId``
  // is seeded once the accounts + reply target load (see the effect
  // below): a reply defaults to the mailbox it was synced INTO so the
  // operator answers from the address it was sent to.
  const [fromAccountId, setFromAccountId] = useState<number | null>(null)
  const [importance, setImportance] = useState<'low' | 'normal' | 'high'>('normal')
  // Change #1210. Public | Private for the saved Sent copy. Private keeps the
  // message inside this mailbox; AI still files and routes it.
  const [visibility, setVisibility] = useState<'public' | 'private'>('public')
  // "Powered by Quotech" marketing footer (FEAT-2026-07-07-GTM-FOOTER-OPT-IN).
  // Seeded from the cached Settings -> Email default (opt-in, OFF unless the
  // operator enabled it); the composer renders it as a visible chip under the
  // body so what-you-see-is-what-ships, and Remove flips it off for THIS send.
  // The resolved boolean rides the send payload as `include_quotech_branding`
  // so the server appends exactly what the composer showed.
  const [includeQuotechBranding, setIncludeQuotechBranding] = useState<boolean>(
    () => getQuotechBrandingDefault(),
  )
  // Once the operator touches the chip, a late server sync must not clobber
  // their explicit choice for this draft.
  const brandingTouchedRef = useRef(false)
  const setIncludeQuotechBrandingUser = (on: boolean) => {
    brandingTouchedRef.current = true
    setIncludeQuotechBranding(on)
  }
  useEffect(() => {
    void syncBrandingDefaultFromServer().then(() => {
      if (!brandingTouchedRef.current) {
        setIncludeQuotechBranding(getQuotechBrandingDefault())
      }
    })
  }, [])
  const [draftResumed, setDraftResumed] = useState(false)
  // Synchronous mirror of `draftResumed`. The state flag flips a render
  // late, which let the new-message signature-seed effect race the resume
  // and overwrite the restored body (including a pasted image) with an
  // empty signature scaffold on first mount. The ref is set the instant we
  // know a draft is being resumed, so template effects bail immediately.
  const draftResumedRef = useRef(false)
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null)
  // True while a cross-device server upsert of the draft is in flight, so the
  // composer can show a live "Saving…" instead of a frozen "Saved Ns ago".
  const [serverSaving, setServerSaving] = useState(false)
  // Forces the relative "Saved Ns ago" label to re-render so it ages live
  // even when no edit is happening (the autosave tick only re-renders on a
  // change). Bumped by an interval that runs only while a draft exists.
  const [savedLabelTick, setSavedLabelTick] = useState(0)
  // Guards setState from the unmount-flush tick's async server upsert, which
  // resolves AFTER the component is gone (dock closed / route away).
  const composeMountedRef = useRef(true)
  useEffect(() => {
    composeMountedRef.current = true
    return () => { composeMountedRef.current = false }
  }, [])
  // A marked-up image (data: URL) handed over from the markup drawer to be
  // embedded INLINE at the top of the body. Captured here, then injected
  // once by the effect below — after any reply quote is in place so it
  // isn't clobbered by the prefill rebuild.
  const [pendingInlineImage, setPendingInlineImage] = useState<string | null>(null)
  const inlineImageInjectedRef = useRef(false)
  // A body lead-in handed over from a cross-page flow (e.g. PDF "Sign &
  // reply" → "Please find the signed document attached."). On a reply/
  // forward we DON'T write this straight into the body — that would make
  // the typing area look non-blank and the quote-prefill below would bail,
  // leaving the operator with no quoted chain ("where is the rest of the
  // email like in Microsoft", 2026-06-08). Instead we stash it here and let
  // the prefill weave it ABOVE the quoted message. Consume-once.
  const pendingBodyPrefixRef = useRef<string | null>(null)
  // When a cross-page flow hands us curated file(s) (the signed PDF from
  // "Sign & reply"), suppress the auto-attach of the source email's own
  // attachments — otherwise the operator gets the signed copy PLUS the
  // unsigned originals piled on ("why are the other two attachments
  // included in the reply chain", 2026-06-08).
  const skipAutoAttachRef = useRef(false)
  // Best-effort sync of the autosaved draft to the operator's
  // Outlook Drafts folder. Failures are silent — the localStorage
  // v3 slot is the source of truth. See useEmailDraftSync.ts.
  const emailDraftSync = useEmailDraftSync({ debounceMs: 8_000 })

  // ---------------------------------------------------------------
  // Server-side draft mirror (cross-device source of truth). Unlike
  // the Outlook sync above, this is OUR database — recipients,
  // subject, the full HTML body INCLUDING inline pasted images, the
  // chosen From account + priority, and file attachments. A draft
  // saved on one PC resumes on another. localStorage / IndexedDB stay
  // as the instant + offline cache; the server is authoritative.
  //   - serverDraftIdRef   : the server row's id once created.
  //   - serverTextSigRef   : content signature gate so we don't POST
  //                          on every 5s tick when nothing changed.
  //   - serverAttMapRef    : name:size → server attachment id, so we
  //                          upload new files / delete removed ones
  //                          and DON'T re-upload bytes we just pulled
  //                          back on resume.
  // All server ops are best-effort: failures never surface to the
  // operator (the local cache keeps the draft safe regardless).
  // ---------------------------------------------------------------
  const serverDraftIdRef = useRef<string | null>(null)
  const serverTextSigRef = useRef<string>('')
  const serverUpsertInFlightRef = useRef(false)
  const serverAttMapRef = useRef<Map<string, string>>(new Map())
  const serverAttSyncInFlightRef = useRef(false)

  // "Drafts auto-delete in 14 days" one-time notice. Shown the first
  // time a draft is actually saved in this session, unless the
  // operator earlier ticked "don't show again".
  const [showExpiryNotice, setShowExpiryNotice] = useState(false)
  const expiryNoticeHandledRef = useRef(false)
  const maybeShowExpiryNotice = () => {
    if (expiryNoticeHandledRef.current) return
    expiryNoticeHandledRef.current = true
    let dismissed = false
    try { dismissed = localStorage.getItem(DRAFT_EXPIRY_NOTICE_KEY) === '1' } catch { /* noop */ }
    if (!dismissed) setShowExpiryNotice(true)
  }
  const dismissExpiryNotice = (forever: boolean) => {
    setShowExpiryNotice(false)
    if (forever) {
      try { localStorage.setItem(DRAFT_EXPIRY_NOTICE_KEY, '1') } catch { /* noop */ }
    }
  }

  /** Build the server upsert payload from the CURRENT compose state.
   *  Shared by the text autosave tick and the attachment-sync effect
   *  (which may need to create the row before the first text tick). */
  const buildServerUpsertPayload = () => ({
    client_ref: slotKey,
    intent: intent || 'new',
    intent_email_id: intentEmailId || null,
    subject,
    body_html: bodyHtml,
    to,
    cc,
    bcc,
    importance,
    email_account_id: fromAccountId,
  })

  const [attachments, setAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isSending, setIsSending] = useState(false)
  // Synchronous double-send guard. `isSending` alone is async state —
  // two rapid clicks (or Enter + click) both read `isSending === false`
  // before the first setState lands, firing TWO POSTs and sending the
  // email twice. The ref flips synchronously inside the first call.
  const sendInFlightRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  // True only when an actual SEND attempt failed (server/network), as
  // opposed to a pre-send validation message ("Please enter a
  // subject"). Drives the "Retry send" affordance in the error banner
  // — BUG-2026-06-12-REPLY-ID-MALFORMED follow-up: a failed send
  // surfaced only as a transient toast, so it looked like the email
  // just sent.
  const [sendFailed, setSendFailed] = useState(false)
  const [success, setSuccess] = useState(false)

  const { draft: aiDraft, loading: aiDrafting, generate: generateAiDraft } = useEmailDraftReply()
  const [showAiDraft, setShowAiDraft] = useState(false)

  const { toast } = useToast()
  const outlook = useOutlookStatus()
  useOutlookOAuthResultToast(toast, outlook.check)
  // `useEmailAccounts` returns the shared-mailbox grants from
  // /api/email-accounts/. Drives banner suppression when the user has a
  // shared inbox even though their personal Outlook OAuth has never been
  // run (Mark Newman's case — see BUG-2026-05-13-OUTLOOK-BANNER-SHARED-
  // MAILBOX in the ledger). Same hook the sidebar uses, so the two stay
  // in sync.
  const { accounts: emailAccounts } = useEmailAccounts()

  // Seed the "From" mailbox once accounts (and, for a reply, the source
  // email) are available — without clobbering an explicit operator pick.
  // Priority (FEAT-2026-07-03-COMPOSE-FROM-PIN, chooseSeedFromAccount):
  // reply → the mailbox the original was synced INTO; then the PINNED
  // default; then the LAST-USED mailbox; then the first connected account.
  // Only real EmailAccount rows (numeric id) are selectable — synthetic
  // legacy facet buckets carry no sendable token.
  useEffect(() => {
    if (fromAccountId !== null) return
    // BUG-2026-07-06-COMPOSE-PIN-HIJACKS-REPLIES: accounts (cached hook)
    // resolve before the reply email (network fetch), so seeding here
    // while the reply is still loading locked in the PINNED mailbox —
    // the `fromAccountId !== null` guard then blocked the re-seed when
    // `synced_by_email` arrived, and replies went out from the wrong
    // address. In reply/forward mode, hold the seed until the source
    // email settles (loading === false covers success AND error — an
    // errored fetch still seeds with the pin/last-used fallback).
    if (intentEmailId && replyEmailLoading) return
    const sendable = emailAccounts.filter(
      (a): a is typeof a & { id: number } => typeof a.id === 'number',
    )
    const seeded = chooseSeedFromAccount({
      sendable,
      syncedInto: bareAddress(replyEmail?.synced_by_email) || null,
      pinnedId: getPinnedFromAccountId(),
      lastId: getLastFromAccountId(),
    })
    if (seeded !== null) setFromAccountId(seeded)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAccounts, replyEmail, replyEmailLoading])

  // ---------------------------------------------------------------
  // Attachment size guard. The Graph simple-send path rejects large
  // payloads with an opaque 413/timeout AFTER a long upload — fail
  // fast at attach time instead. Limits mirror Outlook's practical
  // caps (25MB per file, ~35MB total message).
  // ---------------------------------------------------------------
  const MAX_FILE_BYTES = 25 * 1024 * 1024
  const MAX_TOTAL_BYTES = 35 * 1024 * 1024
  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return
    const accepted: File[] = []
    const rejected: string[] = []
    let total = attachments.reduce((sum, f) => sum + f.size, 0)
    for (const f of incoming) {
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`${f.name} (${(f.size / 1024 / 1024).toFixed(1)}MB > 25MB limit)`)
        continue
      }
      if (total + f.size > MAX_TOTAL_BYTES) {
        rejected.push(`${f.name} (message would exceed the 35MB total limit)`)
        continue
      }
      total += f.size
      accepted.push(f)
    }
    if (accepted.length > 0) setAttachments((prev) => [...prev, ...accepted])
    if (rejected.length > 0) {
      toast(`Couldn't attach: ${rejected.join('; ')}`, 'error')
    }
  }

  // Report live subject + attachment count up to the dock window so its
  // header tracks typing and Close can warn before dropping files.
  useEffect(() => { props.onSubjectChange?.(subject) }, [subject, props.onSubjectChange])
  useEffect(() => { props.onAttachmentsCountChange?.(attachments.length) }, [attachments.length, props.onAttachmentsCountChange])

  // ---------------------------------------------------------------
  // Late seed application (BUG-2026-06-11-COMPOSE-SEED-DROPPED): the
  // dock store now merges a fresh mailto-style seed into an already-
  // open window's meta. Apply it here — but ONLY while the matching
  // field is still pristine, so the operator's typed content always
  // wins over a link click.
  // ---------------------------------------------------------------
  const lastSeedRef = useRef<{ to?: string[]; subject?: string }>({
    to: props.initialToOverride,
    subject: props.initialSubjectOverride,
  })
  useEffect(() => {
    const seedTo = props.initialToOverride
    if (seedTo !== undefined && seedTo !== lastSeedRef.current.to) {
      lastSeedRef.current.to = seedTo
      setTo((cur) => (cur.length === 0 ? seedTo : cur))
    }
    const seedSubject = props.initialSubjectOverride
    if (seedSubject !== undefined && seedSubject !== lastSeedRef.current.subject) {
      lastSeedRef.current.subject = seedSubject
      setSubject((cur) => (cur.trim() === '' ? seedSubject : cur))
    }
  }, [props.initialToOverride, props.initialSubjectOverride])

  // ---------------------------------------------------------------
  // Resume draft (must run before the reply prefill so a stored
  // draft of the same reply-thread wins over the freshly-built
  // quoted block — the operator's edits are always more valuable
  // than the auto-generated quote)
  // ---------------------------------------------------------------
  useEffect(() => {
    try {
      const draft = readDraft(slotKey)
      if (!draft) return
      // Slot is already keyed by intent + intent_email_id, so we
      // don't need the sameIntent check — but keep a defensive
      // assertion in case the storage layer ever returns a draft
      // for the wrong slot (corrupt JSON, manual fiddling, etc.).
      const sameIntent =
        (draft.intent || 'new') === intent &&
        (draft.intent_email_id || null) === (intentEmailId || null)
      if (!sameIntent) return
      setTo(draft.to || [])
      setCc(draft.cc || [])
      setBcc(draft.bcc || [])
      setSubject(draft.subject || '')
      setBodyHtml(draft.body_html || '')
      // Restore From mailbox + priority from the instant cache (BUG-2026-06-23).
      // `from_account_id` is restored only when present so the accounts-seed
      // effect still picks a sensible default for legacy drafts that predate
      // this field; restoring a value also stops that effect from overriding
      // it (it only seeds when `fromAccountId === null`).
      if (draft.from_account_id != null) setFromAccountId(draft.from_account_id)
      if (draft.importance) setImportance(draft.importance)
      draftResumedRef.current = true
      setDraftResumed(true)
      // The editor mounts with `value=''` then syncs on its mount-
      // only effect; we have to push the resumed body in via the
      // imperative `setHtml` after the next paint.
      requestAnimationFrame(() => {
        editorRef.current?.setHtml(draft.body_html || '')
      })
      if ((draft.cc || []).length > 0) setShowCc(true)
      if ((draft.bcc || []).length > 0) setShowBcc(true)
      toast('Draft restored from previous session.', 'success', { persist: false })
    } catch {
      // Corrupted draft — wipe just this slot so it can never block again.
      try { clearDraft(slotKey) } catch { /* noop */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------
  // Resume draft ATTACHMENTS (IndexedDB) — the text half of the
  // draft restores synchronously above, but File bytes live in
  // IndexedDB (localStorage can't hold binary). Only restore when a
  // text draft actually exists for this slot, so the file lifecycle
  // stays tied to the draft. Restoring authoritative attachments also
  // suppresses the reply/forward auto-attach (skipAutoAttachRef) so
  // the operator's curated set isn't piled on top of the source
  // email's originals a second time.
  // ---------------------------------------------------------------
  useEffect(() => {
    const slot = slotKey
    if (!readDraft(slot)) return
    let cancelled = false
    void loadDraftAttachments(slot).then((files) => {
      if (cancelled || files.length === 0) return
      skipAutoAttachRef.current = true
      setAttachments((prev) => {
        // De-dupe against anything already present (a fast auto-attach
        // that beat the IDB read) by name+size so a resume never
        // doubles a file.
        const seen = new Set(prev.map((f) => `${f.name}:${f.size}`))
        const add = files.filter((f) => !seen.has(`${f.name}:${f.size}`))
        return add.length > 0 ? [...prev, ...add] : prev
      })
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------
  // Reply / Reply All / Forward prefill — runs when (a) the
  // referenced email is loaded AND (b) the body doesn't already
  // contain a quoted block (either because the operator just opened
  // a fresh Reply with no stored draft, OR because the resumed draft
  // had only recipients / subject saved but an empty body — see the
  // 2026-05-17 report "the email I am replying to did not come on").
  //
  // Previously we bailed on `draftResumed` unconditionally, which
  // meant a resumed draft with an empty body (operator started a
  // reply, deleted the contents, closed) never got the quote put
  // back. Now we trust the rendered body content as the source of
  // truth — if the quote marker is already in there, we leave the
  // operator's edits alone; if it's missing we (re-)insert it.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!replyEmail) return
    // Wait for the FULL detail to finish loading before quoting. `useEmail`
    // can hand back a cached list row first (body_preview only, no
    // body_html); if we quote that and stamp the quote marker, the guard
    // below blocks re-quoting once the real body_html arrives — so the
    // operator was left with a one-line preview instead of the full
    // message ("can't scroll down and see the previous chain", 2026-05-31).
    if (replyEmailLoading) return
    // Bail ONLY when the operator has actually typed something into the
    // reply area ABOVE the quote — then their words are sacred and we
    // must never rebuild. When the typing area is still blank we fall
    // through and (re)build the scaffold, which covers THREE cases:
    //   - fresh Reply with no stored draft (body empty),
    //   - resumed draft whose body/quote was stripped (quote lost), and
    //   - a STALE draft saved BEFORE the Outlook-spacing fix whose
    //     cramped one-line quote never got upgraded (operator 2026-06-07
    //     "still don't see proper reply body with white space"): the old
    //     guard bailed on any present quote, so the blank-but-cramped
    //     layout was frozen forever. Rebuilding when the typing area is
    //     blank loses nothing (there's nothing typed) and guarantees the
    //     two visible blank lines + divider every time.
    // `typingAreaIsBlank` handles both the reply border marker and the
    // forward header, and stays in lockstep with the markup emitted below.
    if (!typingAreaIsBlank(bodyHtml)) return
    const rawHtml = replyEmail.body_html || ''
    const rawText = replyEmail.body_text || replyEmail.body_preview || ''
    const safeQuoteHtml = rawHtml
      ? sanitizeEmailHtml(rawHtml, replyEmail.attachments)
      : `<pre style="white-space:pre-wrap; font-family:inherit; margin:0;">${
          rawText.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] || c))
        }</pre>`
    // Highlight-to-quote (FEAT-2026-07-06-COMPOSER-MICRO): when the reader
    // captured a text selection at Reply-click time, quote JUST that
    // passage — sanitised through the same pipeline as the full body.
    // Consume-once: a later scaffold rebuild quotes the full chain.
    // Forward ignores this (it always carries the whole message).
    const selectionHtml = takePendingQuoteSelection(String(replyEmail.id))
    const safeSelectionHtml = selectionHtml
      ? sanitizeEmailHtml(selectionHtml, replyEmail.attachments)
      : null
    const attribution = `On ${new Date(replyEmail.received_at).toLocaleString()}, `
      + `${replyEmail.from_name || replyEmail.from_address || 'unknown sender'} wrote:`
    // Outlook-style: a horizontal divider separates the operator's reply
    // space (the blank lines seeded below) from the quoted chain that
    // "goes under" it (operator 2026-06-07). The reply scaffold adds the
    // blank lines ABOVE this divider so there's real room to type.
    // A quoted original that carries layout tables goes in as an OPAQUE
    // block: the prosemirror table schema would normalise its borderless
    // spacer tables into a bordered grid of empty cells (BUG-2026-07-07-
    // EMAIL-QUOTE-TABLE-GRID). Table-free quotes stay inline + editable.
    const quoted = (
      `<hr style="border:none; border-top:1px solid #cbd5e1; margin:16px 0;" />`
      + `<div style="border-left:3px solid #cbd5e1; padding-left:12px; color:#475569; font-size:0.9em;">`
      + `<p style="margin:0 0 8px 0; font-style:italic;">${attribution}</p>`
      + wrapQuoteLayout(safeSelectionHtml || safeQuoteHtml)
      + `</div>`
    )
    // Operator's own signature for this mailbox, else the Quotech-branded
    // default (foreman beaver) so a reply/forward also carries a signature.
    // A real layout signature is wrapped as an OPAQUE block so the editor
    // doesn't shatter its table on seed (BUG-2026-06-29-…-LAYOUT-MANGLED);
    // plain signatures + the beaver default pass through unchanged.
    const sigHtml = resolveSignatureWithDefault(fromAccountEmail, sigIdentity)
    const sigSeed = wrapSignatureLayout(sigHtml)
    // A cross-page lead-in (e.g. "Sign & reply") sits at the very top of the
    // reply, above the typing space + quoted chain. Consume-once so a later
    // rebuild (operator cleared the body) doesn't re-inject it.
    const leadPrefix = pendingBodyPrefixRef.current
    pendingBodyPrefixRef.current = null
    const leadHtml = leadPrefix
      ? `<p>${leadPrefix.replace(/\n/g, '<br>')}</p>`
      : ''
    if (replyToId) {
      // A resumed draft owns its recipients + subject — the operator may
      // have deliberately added/removed people or reworded the subject
      // before closing. Only the BODY scaffold is rebuilt for them
      // (guarded by typingAreaIsBlank above); recipients/subject are
      // reseeded solely on a fresh reply.
      if (!draftResumed) {
        setTo(replyEmail.from_address ? [replyEmail.from_address] : [])
        setSubject(`Re: ${(replyEmail.subject || '').replace(/^(Re:\s*)+/i, '')}`)
      }
      // Sig BEFORE the quote on a reply (operator types above their
      // signature, signature visually separates from the quote). Two
      // VISIBLE blank lines (`<p><br></p>`) give Outlook-style room to
      // type above the quoted chain (operator 2026-06-07: "there is
      // space to reply to and the chain goes under"); `focusStart()`
      // then parks the caret in the first one.
      const replySpace = `<p><br></p><p><br></p>`
      const next = leadHtml + (sigSeed ? `${replySpace}${sigSeed}` : replySpace) + quoted
      setBodyHtml(next)
      requestAnimationFrame(() => {
        editorRef.current?.setHtml(next)
        editorRef.current?.focusStart()
      })
    } else if (replyAllId) {
      // Reply All — recipients = (original from + original to + cc)
      // minus the current user. The mailbox the message was synced
      // INTO (`synced_by_email`) is treated as "me" for self-removal.
      // BE also filters self-replies server-side as a backstop.
      const me = bareAddress(replyEmail.synced_by_email)
      const fromAddr = replyEmail.from_address || ''
      const origTo = (replyEmail.to_addresses || []).map(getRecipientEmail).filter(Boolean)
      const origCc = (replyEmail.cc_addresses || []).map(getRecipientEmail).filter(Boolean)
      const nextTo = dedupeAddresses(
        [fromAddr, ...origTo].filter((a) => bareAddress(a) !== me && !!a),
      )
      const nextCc = dedupeAddresses(
        origCc.filter((a) => bareAddress(a) !== me && bareAddress(a) !== bareAddress(fromAddr) && !!a),
      )
      // See the reply branch — a resumed draft keeps its own
      // recipients + subject; only fresh replies get reseeded.
      if (!draftResumed) {
        setTo(nextTo)
        if (nextCc.length) {
          setCc(nextCc)
          setShowCc(true)
        }
        setSubject(`Re: ${(replyEmail.subject || '').replace(/^(Re:\s*)+/i, '')}`)
      }
      const replySpace = `<p><br></p><p><br></p>`
      const next = leadHtml + (sigSeed ? `${replySpace}${sigSeed}` : replySpace) + quoted
      setBodyHtml(next)
      requestAnimationFrame(() => {
        editorRef.current?.setHtml(next)
        editorRef.current?.focusStart()
      })
    } else if (forwardId) {
      if (!draftResumed) {
        setSubject(`Fwd: ${(replyEmail.subject || '').replace(/^(Fwd?:\s*)+/i, '')}`)
      }
      const fwdHeader = (
        `<p style="margin:0;"><strong>---------- Forwarded message ----------</strong></p>`
        + `<p style="margin:0;">From: ${replyEmail.from_name || ''} `
        + `&lt;${replyEmail.from_address || ''}&gt;</p>`
        + `<p style="margin:0;">Date: ${new Date(replyEmail.received_at).toLocaleString()}</p>`
        + `<p style="margin:0 0 8px 0;">Subject: ${replyEmail.subject || ''}</p>`
      )
      const next = leadHtml + (sigSeed ? `<p><br></p>${sigSeed}<p></p>` : `<p><br></p>`)
        + fwdHeader + wrapQuoteLayout(safeQuoteHtml)
      setBodyHtml(next)
      requestAnimationFrame(() => {
        editorRef.current?.setHtml(next)
        // A fresh forward has NO recipients yet — leave focus in the
        // (auto-focused) To field so the operator fills it before writing,
        // rather than silently sending a forward with an empty To. Only drop
        // the caret into the body when recipients already exist (e.g. a
        // resumed forward draft).
        if (to.length > 0) editorRef.current?.focusStart()
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyEmail, replyEmailLoading, replyToId, replyAllId, forwardId, draftResumed])

  // ---------------------------------------------------------------
  // New-message signature seed — only on a true "New Message" (reply /
  // forward seed their own placement above). PER-ACCOUNT (MR: per-account
  // signatures 2026-06-18): the signature applied is the one for the
  // currently-selected From mailbox, falling back to the single default.
  // When the operator switches From, we SWAP the signature — but only
  // while the body is still the untouched auto-seeded scaffold, so a
  // typed draft is never clobbered. ``sigVersion`` re-runs this once the
  // durable stores hydrate (the synchronous caches start empty).
  // ---------------------------------------------------------------
  const fromAccountEmail = useMemo(() => {
    const acct = emailAccounts.find((a) => a.id === fromAccountId)
    return acct?.email_address || ''
  }, [emailAccounts, fromAccountId])
  // Identity for the branded DEFAULT signature (foreman beaver) applied
  // when the operator has saved none of their own. Prefer the selected
  // From mailbox; fall back to the first sendable account so a reply
  // prefill that runs before the From seed still personalises the block.
  const sigIdentity = useMemo<SignatureIdentity>(() => {
    const sendable = emailAccounts.filter((a) => typeof a.id === 'number')
    const acct = sendable.find((a) => a.id === fromAccountId) || sendable[0]
    return { name: acct?.display_name || '', email: acct?.email_address || '' }
  }, [emailAccounts, fromAccountId])
  const bodyHtmlRef = useRef(bodyHtml)
  useEffect(() => { bodyHtmlRef.current = bodyHtml }, [bodyHtml])
  // Live mirror of the editable compose fields (BUG-2026-06-23). The async
  // cross-device server resume below resolves a beat after mount; without
  // this it could overwrite content the operator started typing/editing in
  // that window (body snapping back, recipients/subject/From/priority
  // reverting, a just-deleted inline image reappearing). Comparing the LIVE
  // values to the local-draft baseline tells us whether the operator has
  // touched the draft since it was restored — for a resumed draft the
  // reply-prefill / signature-seed effects all bail (draftResumed guard), so
  // a divergence here means a genuine edit, not a programmatic one.
  const liveComposeRef = useRef({ to, cc, bcc, subject, bodyHtml })
  useEffect(() => {
    liveComposeRef.current = { to, cc, bcc, subject, bodyHtml }
  }, [to, cc, bcc, subject, bodyHtml])
  const seededSigRef = useRef<string>('')
  const [sigVersion, setSigVersion] = useState(0)

  // Hydrate BOTH durable stores (default + per-account map) once, then
  // bump sigVersion so the seed effect re-resolves with server truth.
  useEffect(() => {
    let alive = true
    void Promise.all([
      hydrateSignatureFromDb(),
      fetchDurableSignaturesByAccount(),
    ]).then(() => { if (alive) setSigVersion((v) => v + 1) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (intent !== 'new' || draftResumed || draftResumedRef.current) return
    // Operator's own signature for the selected From, else the branded
    // default (foreman beaver) so a brand-new message is never blank of a
    // signature. Re-resolves on From change / identity load / durable
    // hydrate (sigVersion), swapping ONLY the untouched seeded scaffold.
    const sig = resolveSignatureWithDefault(fromAccountEmail, sigIdentity)
    const prev = seededSigRef.current
    if (sig === prev) return
    const cur = bodyHtmlRef.current
    // A layout signature is seeded as an OPAQUE block (so the editor can't
    // shatter its table); compare against the SAME wrapped scaffold so the
    // From-switch swap still recognises an untouched body.
    const prevScaffold = prev ? `<p></p><p></p>${wrapSignatureLayout(prev)}` : ''
    // Operator has typed real content (body isn't empty and isn't the
    // last scaffold we seeded): adopt the new signature as the baseline
    // but DON'T touch their draft.
    if (cur && cur !== prevScaffold) { seededSigRef.current = sig; return }
    const next = sig ? `<p></p><p></p>${wrapSignatureLayout(sig)}` : ''
    seededSigRef.current = sig
    setBodyHtml(next)
    requestAnimationFrame(() => editorRef.current?.setHtml(next))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, draftResumed, fromAccountEmail, sigIdentity, sigVersion])

  // ---------------------------------------------------------------
  // Hand-off from the inline-image lightbox / right-click menu /
  // markup drawer (composePendingStore). Same wiring as the v1
  // implementation — pasting an image into the editor body is
  // handled by RichTextEditor; this handles the "Reply with this
  // image" cross-page hand-off.
  // ---------------------------------------------------------------
  useEffect(() => {
    const pending = useComposePendingStore.getState().consume()
    if (!pending) return
    if (pending.files.length > 0) {
      setAttachments((prev) => [...prev, ...pending.files])
      // The hand-off already curated exactly what to send (e.g. the signed
      // PDF) — don't let the source email's originals get auto-attached on
      // top of it.
      skipAutoAttachRef.current = true
      toast(
        `Attached ${pending.files.length} file${pending.files.length === 1 ? '' : 's'}`,
        'success', { persist: false },
      )
    }
    if (pending.inlineImageDataUrl) setPendingInlineImage(pending.inlineImageDataUrl)
    if (pending.subject && !subject) setSubject(pending.subject)
    if (pending.bodyPrefix) {
      const isReplyLike = Boolean(replyToId || replyAllId || forwardId)
      if (isReplyLike) {
        // Defer into the quote-prefill so the quoted chain still builds and
        // the lead-in lands ABOVE it (Outlook-style).
        pendingBodyPrefixRef.current = pending.bodyPrefix
      } else {
        const prefixHtml = `<p>${pending.bodyPrefix.replace(/\n/g, '<br>')}</p>`
        setBodyHtml((b) => prefixHtml + b)
        requestAnimationFrame(() => editorRef.current?.setHtml(prefixHtml + (bodyHtml || '')))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------
  // Inline marked-up image — embed it ONCE at the top of the body.
  // For a reply/forward we wait until the quote prefill has populated
  // the body so the image lands ABOVE the quote and isn't wiped by the
  // prefill's rebuild; for a new message the body may be empty/just a
  // signature and we inject straight away.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!pendingInlineImage || inlineImageInjectedRef.current) return
    const isReplyLike = Boolean(replyToId || replyAllId || forwardId)
    // Hold until the reply quote exists (body non-empty) so we stack on
    // top of it rather than racing the prefill that replaces the body.
    if (isReplyLike && !bodyHtml) return
    inlineImageInjectedRef.current = true
    const imgHtml =
      `<p><img src="${pendingInlineImage}" alt="Marked-up image" `
      + `style="max-width:100%;height:auto;" /></p><p></p>`
    const next = imgHtml + (bodyHtml || '')
    setBodyHtml(next)
    requestAnimationFrame(() => editorRef.current?.setHtml(next))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInlineImage, bodyHtml, replyToId, replyAllId, forwardId])

  // ---------------------------------------------------------------
  // Autosave to localStorage every 5s — quietly, no toast spam.
  // Skips empty drafts so we don't preserve a placeholder shell.
  //
  // BUG-2026-06-11-COMPOSE-AUTOSAVE-GAP: the interval alone left a
  // 0–5s hole — anything typed after the last tick was lost on
  // close/navigation/tab-kill. `flushDraftRef` always points at a
  // tick over the LATEST state and is invoked from (a) effect
  // cleanup (unmount = dock close / route away) and (b) pagehide /
  // visibilitychange-hidden (tab close, app switch on mobile).
  // ---------------------------------------------------------------
  const flushDraftRef = useRef<() => void>(() => {})
  // Set on successful send + explicit discard: those paths just CLEARED
  // the slot, so the unmount flush must not write the draft back.
  const draftClearedRef = useRef(false)
  useEffect(() => {
    const tick = () => {
      if (draftClearedRef.current) return
      // A brand-new compose seeds the saved signature into the body, and a
      // signature is non-empty text — so a naive "body has any text" check
      // treated an UNTOUCHED new message as dirty and autosaved it, leaving a
      // phantom signature-only draft in the Drafts list on EVERY Compose click
      // (operator: blank compose still showed "Saved 1s ago" + drafts piling
      // up). Count the body as user content ONLY when it differs from the
      // seeded signature: its (whitespace-normalised) plain text isn't just the
      // signature's, OR it carries an image the signature doesn't (a pasted
      // screenshot). Operators with no signature keep the exact old behaviour
      // (sigText '' → any text/image counts as content).
      const normText = (h: string) => htmlToPlainText(h).replace(/\s+/g, ' ').trim()
      const imgCount = (h: string) => (h.match(/<img\b/gi) || []).length
      // Compare against the WRAPPED seed: a layout signature lives in the body
      // as an opaque block (no text / no <img> in the markup), so comparing the
      // raw signature would wrongly flag an untouched compose as dirty and pile
      // up phantom signature-only drafts.
      const sigHtml = wrapSignatureLayout(seededSigRef.current || '')
      const bodyHasUserContent =
        (normText(bodyHtml).length > 0 && normText(bodyHtml) !== normText(sigHtml))
        || imgCount(bodyHtml) > imgCount(sigHtml)
      const dirty =
        to.length > 0 || cc.length > 0 || bcc.length > 0
        || subject.trim().length > 0
        || bodyHasUserContent
        || attachments.length > 0
      if (!dirty) return
      const draft: PersistedDraft = {
        to, cc, bcc, subject, body_html: bodyHtml,
        intent, intent_email_id: intentEmailId || null,
        draft_id: draftId,
        // Persist From + priority locally (BUG-2026-06-23) so a reopen/resume
        // before the cross-device server sync lands keeps the operator's
        // choices instead of reverting to the default mailbox / Normal.
        from_account_id: fromAccountId,
        importance,
        saved_at: Date.now(),
      }
      try {
        writeDraft(slotKey, draft)
        setLastSavedAt(draft.saved_at)
        // First successful save this session → surface the 14-day
        // auto-delete notice once (respecting "don't show again").
        maybeShowExpiryNotice()
      } catch {
        // Storage full / disabled — silent fail, the operator
        // hasn't asked for autosave so they shouldn't be punished
        // by it.
      }
      // Mirror to Outlook (best-effort, debounced). The hook
      // swallows network errors and treats "no Outlook connected"
      // (400) as a benign no-op so it's safe to call unconditionally.
      // The plain-text `body` mirrors the HTML so plain-text Outlook
      // clients don't render an empty draft.
      // Mirror EMAIL-SAFE html to Outlook (expands the opaque signature block
      // to its real markup + inlines styles) so the Outlook draft preview
      // shows the signature, not an empty placeholder div.
      const outlookHtml = toEmailHtml(bodyHtml)
      emailDraftSync.scheduleSync({
        subject, body: htmlToPlainText(outlookHtml), body_html: outlookHtml,
        to, cc, bcc, client_ref: slotKey,
        // BUG-2026-07-17-EMAIL-DRAFT-403-WRONG-MAILBOX — bind the
        // provider draft to the From mailbox so the BE uses ITS Graph
        // token (a compose bound to a shared/other mailbox used to
        // fall back to the primary account and 403 ErrorAccessDenied).
        email_account_id: fromAccountId ?? null,
      })
      // Mirror to OUR server (cross-device). Signature-gated so an
      // unchanged draft doesn't re-POST every tick; in-flight guard
      // stops a slow network from stacking requests (the next tick
      // retries). On failure we reset the signature so the change is
      // re-attempted rather than silently dropped.
      const sig = JSON.stringify({
        to, cc, bcc, subject, bodyHtml, importance,
        fromAccountId: fromAccountId ?? null, slotKey,
      })
      if (sig !== serverTextSigRef.current && !serverUpsertInFlightRef.current) {
        serverTextSigRef.current = sig
        serverUpsertInFlightRef.current = true
        if (composeMountedRef.current) setServerSaving(true)
        void upsertServerDraft(buildServerUpsertPayload())
          .then((d) => { serverDraftIdRef.current = d.id })
          .catch(() => { serverTextSigRef.current = '' })
          .finally(() => {
            serverUpsertInFlightRef.current = false
            if (composeMountedRef.current) setServerSaving(false)
          })
      }
    }
    flushDraftRef.current = tick
    const id = window.setInterval(tick, AUTOSAVE_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
      // Unmount = dock window closed or route change — capture the
      // final 0–5s of typing. `draftClearedRef` stops this from
      // resurrecting a slot that send/discard intentionally cleared.
      tick()
    }
  // `buildServerUpsertPayload` / `maybeShowExpiryNotice` are stable in
  // behaviour (close over the same primitives already listed); they're
  // intentionally omitted so the interval isn't torn down every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, bodyHtml, attachments, intent, intentEmailId, slotKey, importance, fromAccountId, draftId, emailDraftSync])

  // ---------------------------------------------------------------
  // Persist draft ATTACHMENTS to IndexedDB (debounced). Kept separate
  // from the text autosave above because writing file bytes is heavier
  // — we only want to touch IDB when the attachment SET actually
  // changes, not on every keystroke-driven 5s tick. A short debounce
  // coalesces the burst of setAttachments calls that a multi-file drop
  // or the reply auto-attach produces. `draftClearedRef` mirrors the
  // text path so a send/discard that just wiped the slot isn't undone.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (draftClearedRef.current) return
    const slot = slotKey
    const id = window.setTimeout(() => {
      void saveDraftAttachments(slot, attachments)
    }, 600)
    return () => window.clearTimeout(id)
  }, [attachments, slotKey])

  // Flush attachments to IndexedDB on UNMOUNT (dock window closed / route
  // change). The debounce above cancels its pending write on cleanup, so a
  // file attached in the last <600ms before close was lost on reopen — and the
  // dock no longer warns about it (attachments DO restore now). This unmount-
  // only effect mirrors the text autosave's tick-on-unmount: it reads the
  // latest attachments + slot via refs and persists once, so closing always
  // captures the final attachment set. Guarded by draftClearedRef so a
  // send/discard that just wiped the slot isn't resurrected.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments
  const slotKeyForFlushRef = useRef(slotKey)
  slotKeyForFlushRef.current = slotKey
  useEffect(() => {
    return () => {
      if (draftClearedRef.current) return
      void saveDraftAttachments(slotKeyForFlushRef.current, attachmentsRef.current)
    }
    // Unmount-only by design — refs carry the latest values in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------
  // Track attachments the operator has REMOVED (BUG-2026-06-25-COMPOSE-
  // SERVER-RESUME-READDS-ATTACHMENT). The cross-device server resume below
  // is a one-shot that downloads every server-side attachment and merges
  // it back — so removing an attachment on a freshly-resumed draft, while
  // that ~500 ms round-trip is still in flight, silently brought the file
  // back. We watch the attachment set and remember any key (name:size)
  // that disappears, so the resume can filter those out and never
  // resurrect a file the operator just deleted. Additions (async IDB
  // restore, reply auto-attach, the resume's own downloads) are ignored —
  // only disappearances count as a removal.
  // ---------------------------------------------------------------
  const removedAttKeysRef = useRef<Set<string>>(new Set())
  const prevAttKeysRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const curr = new Set(attachments.map((f) => `${f.name}:${f.size}`))
    for (const k of prevAttKeysRef.current) {
      if (!curr.has(k)) removedAttKeysRef.current.add(k)
    }
    prevAttKeysRef.current = curr
  }, [attachments])

  // ---------------------------------------------------------------
  // Mirror attachments to the SERVER draft (cross-device). Debounced
  // and diffed against `serverAttMapRef` (name:size → server id):
  // new files are uploaded, removed files are deleted. The resume
  // hydrate pre-seeds the map from the server's own metadata BEFORE it
  // re-adds the downloaded Files, so a freshly-resumed attachment is
  // never re-uploaded. If no server row exists yet (files attached
  // before the first text tick) we create one first. Best-effort —
  // each failure simply retries on the next attachment change.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (draftClearedRef.current) return
    if (attachments.length === 0 && serverAttMapRef.current.size === 0) return
    const id = window.setTimeout(() => {
      if (serverAttSyncInFlightRef.current) return
      serverAttSyncInFlightRef.current = true
      void (async () => {
        try {
          let did = serverDraftIdRef.current
          if (!did) {
            const created = await upsertServerDraft(buildServerUpsertPayload())
            did = created.id
            serverDraftIdRef.current = created.id
          }
          const want = new Map<string, File>()
          for (const f of attachments) want.set(`${f.name}:${f.size}`, f)
          for (const [key, file] of want) {
            if (serverAttMapRef.current.has(key)) continue
            try {
              const meta = await uploadServerDraftAttachment(did, file)
              serverAttMapRef.current.set(key, meta.id)
            } catch { /* leave unmapped → retried on next change */ }
          }
          for (const [key, attId] of [...serverAttMapRef.current]) {
            if (want.has(key)) continue
            try { await deleteServerDraftAttachment(did, attId) } catch { /* noop */ }
            serverAttMapRef.current.delete(key)
          }
        } finally {
          serverAttSyncInFlightRef.current = false
        }
      })()
    }, 900)
    return () => window.clearTimeout(id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, slotKey])

  // ---------------------------------------------------------------
  // Resume from the SERVER draft (cross-device). Runs once on mount,
  // AFTER the synchronous localStorage resume above. We look up the
  // server row for THIS slot and:
  //   - record its id + seed the attachment map (so the sync effect
  //     won't re-upload its files),
  //   - hydrate text ONLY when the server copy is at least as fresh
  //     as the local one (local autosave is instant; the server lags
  //     a tick, so this prefers whichever was edited most recently),
  //   - pull attachment bytes back as Files so they ride the send
  //     path and show as chips.
  // Best-effort: any failure leaves the local cache untouched.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (draftClearedRef.current) return
    let cancelled = false
    void (async () => {
      let drafts: ServerDraft[] = []
      try {
        drafts = await listServerDrafts()
      } catch {
        return
      }
      if (cancelled) return
      const server = drafts.find((d) => d.client_ref === slotKey)
      if (!server) return
      serverDraftIdRef.current = server.id
      for (const a of server.attachments) {
        serverAttMapRef.current.set(`${a.filename}:${a.size}`, a.id)
      }
      const local = readDraft(slotKey)
      const serverTime = Date.parse(server.updated_at) || 0
      const localTime = local?.saved_at || 0
      const hasServerContent = !!(
        server.body_html || server.subject || server.to.length
      )
      // BUG-2026-06-23-COMPOSE-SERVER-RESUME-CLOBBERS-EDITS — if the operator
      // has edited the draft since the synchronous local resume (typed in the
      // body, changed recipients/subject, or deleted a pasted inline image)
      // while this network round-trip was in flight, the on-screen content is
      // newer than anything the server can offer — never overwrite it. We can
      // only judge "edited" when a local baseline existed; if localStorage
      // dropped the draft (quota), there's no baseline and the server copy is
      // the legitimate recovery source, so we let it through.
      const arraysEqual = (a: string[], b: string[]) =>
        a.length === b.length && a.every((v, i) => v === b[i])
      const live = liveComposeRef.current
      const localEdited = !!local && (
        live.bodyHtml !== (local.body_html || '') ||
        live.subject !== (local.subject || '') ||
        !arraysEqual(live.to, local.to || []) ||
        !arraysEqual(live.cc, local.cc || []) ||
        !arraysEqual(live.bcc, local.bcc || [])
      )
      if (localEdited) {
        // Keep the server row id + attachment map so ongoing autosave/sync
        // still targets the right server draft, but DON'T hydrate any field
        // (text or attachments) over the operator's live edits.
        return
      }
      // Recency decides whose body wins. We DELIBERATELY do NOT use an
      // inline-image-count tie-breaker any more (BUG-2026-06-25-COMPOSE-
      // DELETED-IMAGE-RESURRECTS): "server has more images → use server"
      // resurrected an image the operator had just DELETED, because the
      // server copy still carried it until its own delete synced. Recency
      // covers BOTH real cases correctly: (a) operator deletes an image →
      // the post-delete local autosave is the freshest, so local wins and
      // the image stays gone; (b) localStorage dropped the body on a quota
      // overflow → that failed write left `localTime` stale, so the server
      // copy is newer and is pulled back in. The `localEdited` guard above
      // already protects edits made during this round-trip.
      const useServerBody = shouldHydrateServerBody(hasServerContent, serverTime, localTime)
      if (useServerBody) {
        const body = server.body_html || ''
        draftResumedRef.current = true
        setTo(server.to || [])
        setCc(server.cc || [])
        setBcc(server.bcc || [])
        setSubject(server.subject || '')
        setBodyHtml(body)
        setImportance(server.importance || 'normal')
        if (server.email_account_id != null) setFromAccountId(server.email_account_id)
        setDraftResumed(true)
        if ((server.cc || []).length > 0) setShowCc(true)
        if ((server.bcc || []).length > 0) setShowBcc(true)
        requestAnimationFrame(() => editorRef.current?.setHtml(body))
        // Prime the text signature with the JUST-downloaded content so
        // the next autosave tick doesn't immediately echo it back to
        // the server. MUST mirror the tick's signature shape exactly.
        serverTextSigRef.current = JSON.stringify({
          to: server.to || [], cc: server.cc || [], bcc: server.bcc || [],
          subject: server.subject || '', bodyHtml: body,
          importance: server.importance || 'normal',
          fromAccountId: server.email_account_id ?? null, slotKey,
        })
        toast('Draft synced from your account.', 'success', { persist: false })
      }
      if (server.attachments.length > 0) {
        // Authoritative server attachments → suppress the reply/forward
        // auto-attach so the source email's originals aren't piled on.
        skipAutoAttachRef.current = true
        const files = await Promise.all(
          server.attachments.map((a) => downloadServerDraftAttachment(a)),
        )
        if (cancelled) return
        const got = files.filter((f): f is File => f != null)
        if (got.length > 0) {
          setAttachments((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}:${f.size}`))
            // Never re-add a file the operator removed while this resume
            // was in flight (BUG #10).
            const add = attachmentsToAdd(got, seen, removedAttKeysRef.current)
            return add.length > 0 ? [...prev, ...add] : prev
          })
        }
      }
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onHide = () => flushDraftRef.current()
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushDraftRef.current()
    }
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  // Keep the relative "Saved Ns ago" label aging while the composer sits idle
  // (no edits → no autosave re-render). Runs only once a draft has been saved.
  useEffect(() => {
    if (!lastSavedAt) return
    const id = window.setInterval(() => setSavedLabelTick((t) => t + 1), 10_000)
    return () => window.clearInterval(id)
  }, [lastSavedAt])

  const lastSavedLabel = useMemo(() => {
    if (serverSaving) return 'Saving…'
    if (!lastSavedAt) return null
    const ago = Math.max(1, Math.round((Date.now() - lastSavedAt) / 1000))
    return ago < 60 ? `Saved ${ago}s ago` : `Saved ${Math.round(ago / 60)}m ago`
    // `savedLabelTick` is a deliberate re-render trigger (Date.now() inside is
    // not reactive); `serverSaving`/`lastSavedAt` drive the real content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSaving, lastSavedAt, savedLabelTick])

  // First blocking validation error for the current draft, or null when the
  // message is sendable. Pulled out of handleSend so the undo-send hold can
  // surface errors immediately on click (instead of after the countdown), and
  // unit-testable in isolation. handleSend re-checks this, so the hold path
  // stays safe even if state changed mid-window.
  const validateSendInputs = (): string | null => {
    if (to.length === 0) return 'Please enter at least one recipient'
    // Backstop the address format on the COMMITTED chips. RecipientAutocomplete
    // already gates typed/pasted input, but reply/forward seeding and resumed
    // drafts can carry an address that field never vetted — sending it would
    // 400 server-side or bounce silently. Name the offender so it's fixable.
    const badRecipients = invalidEmails([...to, ...cc, ...bcc])
    if (badRecipients.length > 0) {
      const label = badRecipients.length === 1 ? 'address' : 'addresses'
      return `Check the recipient ${label}: ${badRecipients.join(', ')} ${badRecipients.length === 1 ? "doesn't look" : "don't look"} valid`
    }
    if (!subject.trim()) return 'Please enter a subject'
    if (!htmlToPlainText(bodyHtml)) return 'Please enter a message'
    // Auto-attach downloads may still be in flight — sending now would
    // silently ship WITHOUT the files the operator can already see as
    // chips. Block with a visible message instead.
    if (attachmentsBusy) return 'Attachments are still being added - try again in a moment.'
    return null
  }

  const handleSend = async (opts?: {
    /** Resurface train — future ISO fire time. The backend persists the
     *  payload as a scheduled draft (202) and a 1-min beat dispatches it
     *  through the same send pipeline at the fire time. */
    scheduledAt?: string
    /** Resurface train — "remind me if no reply by" ISO time. Stamped on
     *  the sent copy (needs_reply + followup_at); a reply auto-clears,
     *  silence resurfaces the row unread. */
    followupAt?: string
  }) => {
    if (sendInFlightRef.current) return
    const validationError = validateSendInputs()
    if (validationError) { setError(validationError); return }
    // Scheduled sends replay from a stored JSON payload — file bytes
    // can't ride along yet. Surface it at click time, not from a beat
    // task at 3 AM (mirrors the backend's 400 for the same case).
    if (opts?.scheduledAt && attachments.length > 0) {
      setError('Scheduled sends do not support attachments yet - send now, or remove the attachments.')
      return
    }
    const plain = htmlToPlainText(bodyHtml)

    sendInFlightRef.current = true
    setIsSending(true)
    setError(null)
    setSendFailed(false)

    // Kanban card chips the operator embedded in the body → link the sent
    // copy to each card server-side (USER_MANUAL allocation) so it shows
    // under the card. Extract from the RAW editor HTML BEFORE we bake the
    // body — toEmailHtml() flattens the chip markers into static anchors.
    // ARC-1: merged with the host's `extraLinkedCardIds` (the card the
    // inline drawer composer belongs to) so a reply sent from a card
    // links back to that card too.
    const linkedCardIds = mergeLinkedCardIds(
      extractLinkedCardIds(bodyHtml),
      props.extraLinkedCardIds,
    )
    // Email-safe outbound body: TipTap emits class-based HTML (table borders,
    // colours, list markers, formula cells, card chips) that mail clients
    // strip. Re-express as inline styles, bake formulas/chips to static, and
    // DOMPurify-allowlist sanitise. data: images survive so the backend's
    // cid inliner still finds them.
    const outboundHtml = toEmailHtml(bodyHtml)

    const payload: Record<string, unknown> = {
      to,
      subject: subject.trim(),
      body: plain,
      body_html: outboundHtml,
      // Explicit per-send footer decision — the chip the operator saw (or
      // removed) in the composer is exactly what the server will append.
      include_quotech_branding: includeQuotechBranding,
    }
    if (linkedCardIds.length) payload.linked_card_ids = linkedCardIds
    if (cc.length) payload.cc = cc
    if (bcc.length) payload.bcc = bcc
    if (fromAccountId != null) payload.email_account_id = fromAccountId
    if (importance !== 'normal') payload.importance = importance
    if (visibility === 'private') payload.visibility = visibility
    if (replyToId) payload.reply_to_id = replyToId
    if (replyAllId) payload.reply_to_id = replyAllId
    // Forwards carry their source reference too so the backend can
    // thread the sent copy back to the original (job inheritance,
    // conversation grouping).
    if (forwardId) payload.forward_of_id = forwardId
    // The original email owns reply/forward allocation. A host-supplied job
    // link exists for a brand-new compose only, so Job Hub can mount this
    // same frame without inventing a second send path.
    const linkedJobId = replyEmail?.job_id ?? props.extraLinkedJobId
    if (linkedJobId != null) payload.job_id = linkedJobId
    if (opts?.scheduledAt) payload.scheduled_at = opts.scheduledAt
    if (opts?.followupAt) payload.followup_at = opts.followupAt

    // When the operator attached files (e.g. a signed PDF from the
    // "Sign & reply" flow) we MUST switch from JSON to multipart so the
    // bytes actually reach the backend — DRF reads uploads from
    // ``request.FILES``, which a JSON body never populates. Lists are
    // sent as repeated keys (``to``, ``to`` …); DRF's ListField parses
    // those via parse_html_list. ``quotechFetch`` passes FormData
    // through untouched so the browser sets the multipart boundary.
    const requestBody: BodyInit = attachments.length > 0
      ? (() => {
          const fd = new FormData()
          to.forEach((t) => fd.append('to', t))
          cc.forEach((c) => fd.append('cc', c))
          bcc.forEach((b) => fd.append('bcc', b))
          fd.append('subject', subject.trim())
          fd.append('body', plain)
          fd.append('body_html', outboundHtml)
          fd.append('include_quotech_branding', includeQuotechBranding ? 'true' : 'false')
          if (fromAccountId != null) fd.append('email_account_id', String(fromAccountId))
          if (importance !== 'normal') fd.append('importance', importance)
          if (visibility === 'private') fd.append('visibility', visibility)
          if (replyToId) fd.append('reply_to_id', replyToId)
          if (replyAllId) fd.append('reply_to_id', replyAllId)
          if (forwardId) fd.append('forward_of_id', forwardId)
          if (linkedJobId != null) fd.append('job_id', String(linkedJobId))
          // scheduled_at deliberately NOT appended: the attachments guard
          // above blocks scheduled multipart sends before we get here.
          if (opts?.followupAt) fd.append('followup_at', opts.followupAt)
          linkedCardIds.forEach((id) => fd.append('linked_card_ids', id))
          attachments.forEach((file) => fd.append('attachments', file, file.name))
          return fd
        })()
      : JSON.stringify(payload)

    const requestId = newRequestId('send')
    const startedAt = Date.now()
    try {
      const res = await emailApiFetch('/api/emails/send/', {
        method: 'POST', body: requestBody,
      })
      const data = await res.json().catch(() => ({} as { error?: string; method?: string; outbox_id?: number }))
      if (!res.ok) {
        const errMsg = data?.error || `Failed to send email (${res.status})`
        void reportClientIssue({
          source: 'client.email.send', severity: 'error',
          reason: `http_${res.status}`, message: errMsg, requestId,
          context: {
            to_count: to.length, cc_count: cc.length, bcc_count: bcc.length,
            attachments: attachments.length, reply_to_id: intentEmailId,
            http_status: res.status,
            outbox_id: (data as { outbox_id?: number }).outbox_id,
            duration_ms: Date.now() - startedAt,
            body_html_chars: bodyHtml.length,
          },
          detail: typeof data === 'object' ? JSON.stringify(data) : undefined,
        })
        throw new Error(errMsg)
      }
      const method = (data as { method?: string }).method
      const n = to.length + cc.length + bcc.length
      const recipientLabel = `${n} ${n === 1 ? 'recipient' : 'recipients'}`
      const wasScheduled = Boolean((data as { scheduled?: boolean }).scheduled)
      if (wasScheduled) {
        // 202 — nothing left the mailbox yet; the beat task fires it at
        // the scheduled time. Same cleanup path as a real send (the
        // scheduled draft is a SEPARATE server row keyed
        // `scheduled:<uuid>`, so discarding the compose draft below
        // cannot cancel the scheduled send).
        toast(
          `Scheduled — will send ${formatResurfaceTime(
            (data as { scheduled_at?: string }).scheduled_at
            ?? opts?.scheduledAt ?? '',
          ) || 'later'}.`,
          'success',
        )
      } else if (method === 'graph') {
        toast(`Email sent to ${recipientLabel} via Outlook.`, 'success')
      } else if (method === 'smtp') {
        toast(
          `Email sent to ${recipientLabel} via SMTP fallback. Connect Microsoft to send from your own mailbox.`,
          'warning',
        )
        void reportClientIssue({
          source: 'client.email.send', severity: 'warning',
          reason: 'smtp_fallback',
          message: `Email sent via SMTP fallback (Outlook not connected) - ${recipientLabel}`,
          requestId,
          context: {
            to_count: to.length, cc_count: cc.length, bcc_count: bcc.length,
            attachments: attachments.length, duration_ms: Date.now() - startedAt,
          },
        })
      } else {
        toast(`Email sent to ${recipientLabel}.`, 'success')
      }
      // Remember the mailbox this send used so the next compose on THIS
      // device defaults to it immediately (the server copy is written by
      // the send endpoint — FEAT-2026-07-03-COMPOSE-FROM-PIN).
      recordLastFromAccountLocal(fromAccountId)
      // Sent successfully → discard the saved draft so a refresh
      // of /email/compose comes up clean. Flag it so the unmount
      // autosave flush can't write the draft straight back.
      draftClearedRef.current = true
      try { clearDraft(slotKey) } catch { /* noop */ }
      void clearDraftAttachments(slotKey)
      // Drop the server-side draft too (cross-device) so the sent
      // message doesn't linger as a draft on another PC.
      if (serverDraftIdRef.current) {
        void deleteServerDraft(serverDraftIdRef.current)
        serverDraftIdRef.current = null
      }
      // Fire-and-forget — DELETE the Outlook Drafts copy so the
      // sent message doesn't linger as a ghost draft on the
      // operator's other devices.
      void emailDraftSync.clear()
      // Dock mode closes immediately; page mode shows the splash for 1.5s then navigates.
      if (props.onSent) {
        props.onSent()
      } else {
        setSuccess(true)
        setTimeout(() => navigate(emailPath('/inbox')), 1500)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send email'
      setError(message)
      setSendFailed(true)
      // Dock-mode: pop a minimized window back open so the failure is
      // visible — without this the only signal was a transient toast
      // and the operator reasonably assumed the email had sent.
      props.onSendFailed?.()
      toast(message, 'error')
      if (!(err instanceof Error && err.message.startsWith('Failed to send email ('))) {
        void reportClientIssue({
          source: 'client.email.send', severity: 'error',
          reason: 'network', message, requestId,
          context: {
            to_count: to.length, cc_count: cc.length, bcc_count: bcc.length,
            attachments: attachments.length, duration_ms: Date.now() - startedAt,
          },
          detail: err instanceof Error && err.stack ? err.stack : undefined,
        })
      }
      void outlook.check()
    } finally {
      sendInFlightRef.current = false
      setIsSending(false)
    }
  }

  const handleDiscard = () => {
    const dirty =
      to.length > 0 || cc.length > 0 || bcc.length > 0
      || subject.length > 0 || htmlToPlainText(bodyHtml).length > 0
    if (dirty && !confirm('Discard this draft?')) return
    draftClearedRef.current = true
    try { clearDraft(slotKey) } catch { /* noop */ }
    void clearDraftAttachments(slotKey)
    // Drop the server-side draft too (cross-device).
    if (serverDraftIdRef.current) {
      void deleteServerDraft(serverDraftIdRef.current)
      serverDraftIdRef.current = null
    }
    // Fire-and-forget — DELETE the Outlook Drafts copy so the
    // operator's Outlook web/mobile drafts list matches what they
    // see in SAM.
    void emailDraftSync.clear()
    if (props.onClose) {
      props.onClose()
    } else {
      navigate(emailPath('/inbox'))
    }
  }

  // Signature editing happens in SignatureEditorModal (rich-text WYSIWYG)
  // — the old window.prompt() was a single-line box expecting raw HTML,
  // which made real multi-line signatures impossible to enter ("can't
  // put signature properly here", 2026-06-10).
  const [signatureModalOpen, setSignatureModalOpen] = useState(false)
  const handleSaveSignature = () => setSignatureModalOpen(true)
  const closeSignatureModal = () => setSignatureModalOpen(false)

  const onSignatureSaved = (_html: string, outcome: 'saved' | 'cleared') => {
    toast(
      outcome === 'saved'
        ? 'Signature saved. It will be added to new messages automatically.'
        : 'Signature cleared.',
      'success',
    )
  }

  const onSignatureError = (message: string) => toast(message, 'error')

  /** Drop the (just-saved) signature into the CURRENT draft at the
   *  caret — the modal's "Save & insert now" action. */
  const insertSignatureNow = (html: string) => {
    if (!html) return
    // Wrap defensively: the modal hands over the opaque-block form already,
    // but any other caller passing RAW signature HTML must not feed a layout
    // signature (tables / pasted images) to the table schema - it shatters
    // (BUG-2026-06-29) and images can be dropped (BUG-2026-07-07).
    const seed = hasSignatureBlock(html) ? html : wrapSignatureLayout(html)
    editorRef.current?.insertHtml(`<p></p>${seed}`)
    toast('Signature inserted into this message.', 'success', { persist: false })
  }

  // ---------------------------------------------------------------
  // Quoted-original-email helpers — shared between the automatic
  // reply/forward prefill effect above AND the manual "Insert
  // original" button surfaced in the intent banner. Single source of
  // truth so the two paths always emit the same markup (operator
  // can't end up with two visually different quote styles depending
  // on how they got there).
  //
  // `QUOTE_MARKER_STYLE` is a literal substring of the wrapper div
  // emitted by `buildQuoteHtml` — used by `quotedBlockPresent` to
  // detect whether the body already contains the quote. Keep these
  // in lockstep when the quote style changes.
  // ---------------------------------------------------------------
  const QUOTE_MARKER_STYLE = 'border-left:3px solid #cbd5e1'

  const buildQuoteHtml = (): string | null => {
    if (!replyEmail) return null
    const rawHtml = replyEmail.body_html || ''
    const rawText = replyEmail.body_text || replyEmail.body_preview || ''
    const safeQuoteHtml = rawHtml
      ? sanitizeEmailHtml(rawHtml, replyEmail.attachments)
      : `<pre style="white-space:pre-wrap; font-family:inherit; margin:0;">${
          rawText.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] || c))
        }</pre>`
    const attribution = `On ${new Date(replyEmail.received_at).toLocaleString()}, `
      + `${replyEmail.from_name || replyEmail.from_address || 'unknown sender'} wrote:`
    if (intent === 'forward') {
      return (
        `<p style="margin:0;"><strong>---------- Forwarded message ----------</strong></p>`
        + `<p style="margin:0;">From: ${replyEmail.from_name || ''} `
        + `&lt;${replyEmail.from_address || ''}&gt;</p>`
        + `<p style="margin:0;">Date: ${new Date(replyEmail.received_at).toLocaleString()}</p>`
        + `<p style="margin:0 0 8px 0;">Subject: ${replyEmail.subject || ''}</p>`
        + wrapQuoteLayout(safeQuoteHtml)
      )
    }
    return (
      `<hr style="border:none; border-top:1px solid #cbd5e1; margin:16px 0;" />`
      + `<div style="${QUOTE_MARKER_STYLE}; padding-left:12px; color:#475569; font-size:0.9em;">`
      + `<p style="margin:0 0 8px 0; font-style:italic;">${attribution}</p>`
      + wrapQuoteLayout(safeQuoteHtml)
      + `</div>`
    )
  }

  // The legacy `QUOTE_MARKER_STYLE` div is flattened by the TipTap editor on
  // round-trip, so we ALSO key off signals that survive: the reply `<hr>`
  // divider and the forward header text. Keeps the "Insert original quote"
  // affordance from re-offering a quote that's already in the body.
  const quotedBlockPresent = bodyHtml.includes(QUOTE_MARKER_STYLE)
    || bodyHtml.includes('Forwarded message')
    || bodyHtml.includes('<hr')
    || bodyHtml.includes('data-quote=')

  const insertOriginalQuote = () => {
    const quote = buildQuoteHtml()
    if (!quote) {
      toast('Original email not loaded yet.', 'error', { persist: false })
      return
    }
    // Append to the existing body — operator may have already typed
    // their reply, we mustn't blow it away. Editor's `setHtml` will
    // become the new value for both DOM and React state.
    const nextBody = bodyHtml + quote
    setBodyHtml(nextBody)
    requestAnimationFrame(() => editorRef.current?.setHtml(nextBody))
    toast('Inserted original email at the bottom of the body.', 'success', { persist: false })
  }

  // ---------------------------------------------------------------
  // Original-message attachments (reply / forward).
  //
  // A reply does NOT inherit the source email's files, so when the
  // operator replies to e.g. a contract email ("send us the
  // countersigned version as pdf") the contract they need to act on
  // was invisible in compose (operator 2026-06-07: "the file is still
  // not visible next to the line of draft reply with AI"). We surface
  // the source email's REAL (non-inline, downloadable) attachments as
  // chips at the top of the body so they're visible up-front, and let
  // the operator pull any of them INTO the reply with one click — we
  // fetch the bytes via the same authed download endpoint the reader
  // uses and wrap them as a `File`, which then flows through the normal
  // multipart send path. Inline images are excluded (they belong in the
  // quoted body, not as loose files).
  // ---------------------------------------------------------------
  const originalAttachments = useMemo<EmailAttachment[]>(() => {
    const list = replyEmail?.attachments || []
    return list.filter((a) => !a.is_inline && !!a.download_url)
  }, [replyEmail])

  const [attachedOriginalIds, setAttachedOriginalIds] = useState<Set<string>>(new Set())
  const [attachingOriginalId, setAttachingOriginalId] = useState<string | null>(null)
  // True while the auto-attach batch download is still in flight —
  // handleSend blocks on this so a fast Send can't ship without the
  // files the operator already sees as chips.
  const [autoAttachPending, setAutoAttachPending] = useState(false)
  const attachmentsBusy = autoAttachPending || attachingOriginalId !== null

  /** Fetch one source attachment's bytes via the authed download
   *  endpoint and wrap them as a `File`. Pure-ish I→O: attachment ->
   *  File | null (null on any failure). */
  const downloadAttachmentFile = async (att: EmailAttachment): Promise<File | null> => {
    if (!att.download_url) return null
    try {
      const res = await emailApiFetch(att.download_url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      return new File(
        [blob],
        att.filename || 'attachment',
        { type: att.mime_type || blob.type || 'application/octet-stream' },
      )
    } catch {
      return null
    }
  }

  // Manual fallback — only used to RETRY a file whose auto-attach
  // download failed (those stay in the lane with an "Attach" chip).
  const attachOriginalAttachment = async (att: EmailAttachment) => {
    if (!att.download_url || attachedOriginalIds.has(att.id) || attachingOriginalId) return
    setAttachingOriginalId(att.id)
    const file = await downloadAttachmentFile(att)
    if (file) {
      setAttachments((prev) => [...prev, file])
      setAttachedOriginalIds((prev) => new Set(prev).add(att.id))
      toast(`Attached ${att.filename} to your reply.`, 'success', { persist: false })
    } else {
      toast(`Could not attach ${att.filename}. Try downloading it from the original email.`, 'error')
    }
    setAttachingOriginalId(null)
  }

  // AUTO-ATTACH the source email's files onto the reply/forward — the
  // operator (2026-06-07) shouldn't have to click "Attach" to send back
  // the contract they're replying about ("why did the file have to have
  // attach, it should auto attach"). Runs ONCE per source email per
  // compose session (a ref guard, so re-renders / draft autosave don't
  // re-add, and a file the operator deliberately removes stays removed).
  // Failures leave that file in the lane as a manual "Attach" retry.
  const autoAttachedEmailRef = useRef<string | null>(null)
  useEffect(() => {
    if (intent === 'new' || !intentEmailId) return
    // A curated hand-off (Sign & reply) already attached exactly what to
    // send — don't pile the source email's originals on top.
    if (skipAutoAttachRef.current) return
    if (originalAttachments.length === 0) return
    if (autoAttachedEmailRef.current === intentEmailId) return
    autoAttachedEmailRef.current = intentEmailId

    let cancelled = false
    void (async () => {
      const pending = originalAttachments.filter((a) => !attachedOriginalIds.has(a.id))
      if (pending.length === 0) return
      setAutoAttachPending(true)
      const results = await Promise.all(
        pending.map(async (att) => ({ att, file: await downloadAttachmentFile(att) })),
      ).finally(() => { if (!cancelled) setAutoAttachPending(false) })
      if (cancelled) return
      const ok = results.filter((r): r is { att: EmailAttachment; file: File } => r.file != null)
      if (ok.length === 0) return
      setAttachments((prev) => [...prev, ...ok.map((r) => r.file)])
      setAttachedOriginalIds((prev) => {
        const next = new Set(prev)
        ok.forEach((r) => next.add(r.att.id))
        return next
      })
      toast(
        ok.length === 1
          ? `Attached ${ok[0].att.filename} from the original email.`
          : `Attached ${ok.length} files from the original email.`,
        'success',
        { persist: false },
      )
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, intentEmailId, originalAttachments])


  return {
    addFiles,
    aiDraft,
    aiDrafting,
    attachedOriginalIds,
    attachingOriginalId,
    attachments,
    attachmentsBusy,
    attachOriginalAttachment,
    bcc,
    bodyHtml,
    cc,
    closeSignatureModal,
    editorRef,
    emailAccounts,
    error,
    fileInputRef,
    fromAccountId,
    setFromAccountId,
    importance,
    setImportance,
    visibility,
    setVisibility,
    includeQuotechBranding,
    setIncludeQuotechBranding: setIncludeQuotechBrandingUser,
    generateAiDraft,
    handleDiscard,
    originalAttachments,
    handleSaveSignature,
    handleSend,
    validateSendInputs,
    insertSignatureNow,
    onSignatureError,
    onSignatureSaved,
    signatureModalOpen,
    insertOriginalQuote,
    intent,
    isSending,
    lastSavedLabel,
    quotedBlockPresent,
    refetchReplyEmail,
    replyEmail,
    replyEmailError,
    replyEmailLoading,
    sendFailed,
    setAttachments,
    setBcc,
    setBodyHtml,
    setCc,
    setShowAiDraft,
    setShowBcc,
    setShowCc,
    setSubject,
    setTo,
    showAiDraft,
    showBcc,
    showCc,
    showExpiryNotice,
    dismissExpiryNotice,
    subject,
    success,
    to,
  }
}
